// packages/web/src/components/account/auth-signup-form.tsx
/**
 * Signup form — stage 1 of two-stage signup (19BO+ Phase B).
 *
 * Email + password (8+ chars) + optional invite code. Calls authApi.signup
 * (the REQUEST stage) → switches to 'signupSent' mode (no user created yet,
 * no login — the user clicks the email link to complete stage 2).
 *
 * Anti-enumeration: signup returns the same { ok } shape whether the email is
 * fresh or already registered. We do NOT surface a 409/emailTaken error here
 * (that was an enumeration oracle) — every successful request shows the same
 * "check your email" confirmation. Only network/4xx-from-validation errors
 * surface to the user.
 *
 * Marketing banner shows the 1,000-point welcome bonus.
 */

import { useState } from 'react';
import { useT } from '../../i18n/use-t.js';
import { authApi } from '../../api/auth.js';
import { IngestApiError } from '../../lib/api-client.js';
import type { AuthMode } from './auth-modal.js';

interface Props {
  onSwitchMode: (m: AuthMode) => void;
  onClose: () => void;
}

type PwStrength = 0 | 1 | 2 | 3;

function strengthOf(pw: string): PwStrength {
  if (pw.length < 8) return 0;
  let s = 1;
  if (/[A-Z]/.test(pw) && /\d/.test(pw)) s = 2;
  if (pw.length >= 12 && /[^A-Za-z0-9]/.test(pw)) s = 3;
  return s as PwStrength;
}

const STRENGTH_KEY = ['auth.pwMinLength', 'auth.pwStrengthWeak', 'auth.pwStrengthMedium', 'auth.pwStrengthStrong'] as const;

export function AuthSignupForm({ onSwitchMode }: Props) {
  const t = useT();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const strength = strengthOf(password);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (loading) return;
    setError(null);
    if (password.length < 8) {
      setError(t('auth.weakPassword'));
      return;
    }
    if (!agree) {
      setError(t('auth.agreeTerms'));
      return;
    }
    setLoading(true);
    try {
      // 19BO+ Phase B: request stage only. No user is returned; the response
      // shape is identical for fresh + taken emails (anti-enumeration).
      await authApi.signup(
        email.trim().toLowerCase(),
        password,
        undefined,
        inviteCode.trim().toUpperCase() || undefined,
      );
      // Switch to the "check your email" confirmation view. Do NOT log in —
      // the user is created only after they click the email link.
      onSwitchMode('signupSent');
    } catch (err) {
      // 19BO+ Phase B anti-enumeration: do NOT special-case 409/emailTaken
      // (that leaked email existence). Only surface validation errors (400
      // weak password / invalid email / bad invite) + network failures. A
      // 200 from the server always shows the "email sent" confirmation.
      if (err instanceof IngestApiError) {
        if (err.status === 400) setError(t('auth.invalidEmail'));
        else setError(err.response?.message || 'Signup failed');
      } else {
        setError(err instanceof Error ? err.message : 'Signup failed');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <div className="auth-head">
        <span className="auth-head-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <line x1="19" y1="8" x2="19" y2="14" />
            <line x1="22" y1="11" x2="16" y2="11" />
          </svg>
        </span>
        <h2>{t('auth.createAccount')}</h2>
      </div>
      <form className="auth-body" onSubmit={submit}>
        <div className="auth-promo">
          <span className="auth-promo-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
              <rect x="3" y="8" width="18" height="4" rx="1" />
              <path d="M12 8v13M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7" />
              <path d="M7.5 8a2.5 2.5 0 0 1 0-5C11 3 12 8 12 8M16.5 8a2.5 2.5 0 0 0 0-5C13 3 12 8 12 8" />
            </svg>
          </span>
          <div>
            <p className="auth-promo-title">{t('auth.welcomeBonus')}</p>
            <p className="auth-promo-sub">{t('auth.welcomeBonusSub')}</p>
          </div>
        </div>
        <div className="auth-field">
          <label className="auth-field-label" htmlFor="auth-signup-email">{t('auth.email')}</label>
          <input
            id="auth-signup-email"
            className={`auth-field-input${error === t('auth.invalidEmail') ? ' has-error' : ''}`}
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
          />
        </div>
        <div className="auth-field">
          <label className="auth-field-label" htmlFor="auth-signup-pw">{t('auth.password')}</label>
          <div className="auth-field-input-wrap">
            <input
              id="auth-signup-pw"
              className={`auth-field-input${error === t('auth.weakPassword') ? ' has-error' : ''}`}
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          {password.length > 0 && (
            <>
              <div className="auth-pw-strength">
                {[0, 1, 2, 3].map((i) => (
                  <span
                    key={i}
                    className={`auth-pw-bar${i < strength ? ` ${['weak', 'weak', 'medium', 'strong'][strength]}` : ''}`}
                  />
                ))}
              </div>
              <div className="auth-pw-label">{t(STRENGTH_KEY[strength])}</div>
            </>
          )}
        </div>
        <div className="auth-field">
          <label className="auth-field-label" htmlFor="auth-signup-invite">{t('auth.inviteCode')}</label>
          <input
            id="auth-signup-invite"
            className="auth-field-input"
            type="text"
            placeholder="ALICE-ABC123"
            value={inviteCode}
            onChange={(e) => setInviteCode(e.target.value)}
          />
        </div>
        <label className="auth-checkbox">
          <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
          {t('auth.agreeTerms')}
        </label>
        {error && (
          <div className="auth-field-error">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="11" height="11">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            {error}
          </div>
        )}
        <button type="submit" className={`auth-btn-primary${loading ? ' is-loading' : ''}`} disabled={loading}>
          {t('auth.signUp')}
        </button>
        <div className="auth-switch">
          {t('auth.haveAccount')}{' '}
          <button type="button" onClick={() => onSwitchMode('login')}>{t('auth.welcome')} →</button>
        </div>
      </form>
    </>
  );
}
